import {useEffect, useId, useMemo, useRef, useState, type KeyboardEvent} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {AlertCircle, ChevronLeft, Delete, Grid3x3, Loader2, MicOff, Phone, Search, UserPlus, WifiOff, X} from 'lucide-react';
import type {RootState} from '../../../store/store';
import {setPhoneNumber} from '../../../store/features/calls/dialerSlice';
import {selectUserId} from '../../../store/features/Auth/authSlice';
import {useSearchContactSuggestionsQuery, type ContactIdentity} from '../../../store/features/Contacts/contactsService';
import {useFetchUsersQuery, type UserElement} from '../../../store/features/Users/userService';
import {useFetchMsisdnsListQuery} from '../../../store/features/MsisdnSettings/msisdnSettingsService';
import type {Msisdn} from '../../../store/features/Msisdns/msisdnService';
import {useDTMF} from '../../../components/Dialer/hooks/DTMFToneHook';
import {getFormatedPhoneNumber} from '../../../utilis/msisdnUtilis';
import {CallAvatar} from '../components/CallAvatar';
import {KEYPAD_KEYS} from '../components/keypadKeys';
import {selectCallSurfaceState, setDialerRefusal, type DialerNotice} from '../store/callSurfaceSlice';
import {LinePicker} from './LinePicker';
import {formatDialable, looksLikeNumber, regionOfLine, toDialableNumber} from './dialerNumbers';
import {useDialerActions} from './useDialerActions';
import {useRtcFeatures} from '../../groupCalls/hooks/useGroupCall';
import {startTeamGroupCall} from '../team/startTeamCall';
import {dialerMessages as m} from './dialerMessages';
import {refusalMatches} from './dialerRefusal';
import {RefusalRow} from './RefusalRow';

const MIN_SEARCH = 2;
const MAX_TEAM = 4;
const MAX_CONTACTS = 5;

type DialerOption =
    | {readonly kind: 'number'; readonly id: string; readonly e164: string}
    | {readonly kind: 'team'; readonly id: string; readonly user: UserElement}
    | {readonly kind: 'contact'; readonly id: string; readonly contact: ContactIdentity};

const fullName = (first?: string, last?: string): string => [first, last].filter(Boolean).join(' ');

const optionName = (option: DialerOption): string =>
    option.kind === 'number'
        ? formatDialable(option.e164)
        : option.kind === 'team'
            ? fullName(option.user.firstName, option.user.lastName)
            : fullName(option.contact.firstName, option.contact.lastName) || getFormatedPhoneNumber(option.contact.phoneNumber, false);

type Notice = DialerNotice | 'offline' | 'mic' | 'no_line';

const NOTICE_TEXT = {
    in_call: m.alreadyInCall,
    sip_error: m.sipError,
    no_number: m.noLine,
    no_line: m.noLine,
    offline: m.offline,
    mic: m.micBlocked,
} as const;

const NoticeRow = ({notice}: {notice: Notice}) => {
    const intl = useIntl();
    const Icon = notice === 'offline' ? WifiOff : notice === 'mic' ? MicOff : AlertCircle;
    return (
        <div role="status" className="flex items-start gap-2 rounded-xl bg-[var(--phq-call-warning-tint)] px-3 py-2 text-[13px] font-medium text-[var(--phq-call-warning-ink)]">
            <Icon size={16} className="mt-0.5 shrink-0" aria-hidden="true"/>
            <span>{intl.formatMessage(NOTICE_TEXT[notice])}</span>
        </div>
    );
};

const useDialerOptions = (text: string, line: Msisdn | undefined): readonly DialerOption[] => {
    const userId = useSelector(selectUserId);
    const phrase = text.trim();
    const searching = phrase.length >= MIN_SEARCH && !looksLikeNumber(phrase);
    const {data: contacts} = useSearchContactSuggestionsQuery({phrase}, {skip: !searching});
    const {data: users} = useFetchUsersQuery({}, {skip: !searching});
    return useMemo(() => {
        const options: DialerOption[] = [];
        const e164 = looksLikeNumber(phrase) ? toDialableNumber(phrase, regionOfLine(line?.msisdn)) : undefined;
        if (e164) {
            options.push({kind: 'number', id: `number:${e164}`, e164});
        }
        if (searching) {
            const needle = phrase.toLowerCase();
            (users ?? [])
                .filter(user => String(user.userId ?? user.id) !== String(userId))
                .filter(user => fullName(user.firstName, user.lastName).toLowerCase().includes(needle))
                .slice(0, MAX_TEAM)
                .forEach(user => options.push({kind: 'team', id: `team:${user.userId ?? user.id}`, user}));
            (contacts ?? [])
                .slice(0, MAX_CONTACTS)
                .forEach(entry => options.push({kind: 'contact', id: `contact:${entry.primary.contactId}`, contact: entry.primary}));
        }
        return options;
    }, [contacts, line?.msisdn, phrase, searching, userId, users]);
};

interface DialerBubbleProps {
    readonly onClose: () => void;
}

export const DialerBubble = ({onClose}: DialerBubbleProps) => {
    const intl = useIntl();
    const dispatch = useDispatch();
    const listId = useId();
    const input = useRef<HTMLInputElement>(null);
    const text = useSelector((state: RootState) => state.dialer.phoneNumber) ?? '';
    const line = useSelector((state: RootState) => state.dialer.selectedPhoneNumber) as Msisdn | undefined;
    const isOffline = useSelector((state: RootState) => state.dialer.isOffline);
    const micRequired = useSelector((state: RootState) => state.dialer.micRequired);
    const {dialerNotice, dialerRefusal} = useSelector(selectCallSurfaceState);
    const userId = useSelector(selectUserId);
    const {data: lines, isFetching: linesLoading} = useFetchMsisdnsListQuery({userId});
    const [view, setView] = useState<'search' | 'keypad'>('search');
    const [active, setActive] = useState(0);
    const [invalid, setInvalid] = useState(false);
    const options = useDialerOptions(text, line);
    const {callNumber, callTeamMember} = useDialerActions();
    const {playToneForDuration} = useDTMF({volume: 0.1});
    const groupCalls = useRtcFeatures().groupCalls;
    const [picked, setPicked] = useState<readonly UserElement[]>([]);
    const [startingGroup, setStartingGroup] = useState(false);
    const [groupFailed, setGroupFailed] = useState(false);
    const pickedIds = new Set(picked.map(user => String(user.userId ?? user.id)));

    const pick = (user: UserElement) => {
        if (!pickedIds.has(String(user.userId ?? user.id))) {
            setPicked(current => [...current, user]);
        }
        setText('');
        input.current?.focus();
    };

    const unpick = (user: UserElement) => setPicked(current => current.filter(other => (other.userId ?? other.id) !== (user.userId ?? user.id)));

    const callPicked = async () => {
        if (picked.length === 1) {
            callTeamMember(picked[0]);
            return;
        }
        setStartingGroup(true);
        setGroupFailed(false);
        const outcome = await startTeamGroupCall(picked.map(user => Number(user.userId ?? user.id)), 'audio');
        setStartingGroup(false);
        if (outcome === 'started') {
            setPicked([]);
            onClose();
        } else {
            setGroupFailed(outcome === 'failed');
        }
    };

    const notice: Notice | undefined = isOffline
        ? 'offline'
        : micRequired
            ? 'mic'
            : !linesLoading && (lines?.data?.length ?? 0) === 0
                ? 'no_line'
                : dialerNotice;

    useEffect(() => {
        input.current?.focus();
    }, [view]);

    useEffect(() => {
        setActive(0);
        setInvalid(false);
    }, [text]);

    const refusal = refusalMatches(dialerRefusal, text) ? dialerRefusal : undefined;
    useEffect(() => {
        if (dialerRefusal && !refusalMatches(dialerRefusal, text)) {
            dispatch(setDialerRefusal(undefined));
        }
    }, [dialerRefusal, dispatch, text]);

    const setText = (value: string) => dispatch(setPhoneNumber(value));

    const phoneBlocked = !!notice && notice !== 'in_call';
    const isBlocked = (option: DialerOption | undefined): boolean =>
        option?.kind === 'team' ? notice === 'offline' : phoneBlocked;

    const choose = (option: DialerOption | undefined) => {
        if (!option) {
            if (text.trim()) {
                setInvalid(true);
            }
            return;
        }
        if (isBlocked(option)) {
            return;
        }
        if (option.kind === 'team') {
            callTeamMember(option.user);
        } else if (option.kind === 'contact') {
            callNumber(`+${String(option.contact.phoneNumber).replace(/^\+/, '')}`);
        } else {
            callNumber(option.e164);
        }
    };

    const dialTyped = () => {
        if (phoneBlocked) {
            return;
        }
        const e164 = toDialableNumber(text, regionOfLine(line?.msisdn));
        if (e164) {
            callNumber(e164);
        } else {
            setInvalid(true);
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setActive(current => (options.length ? (current + step + options.length) % options.length : 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (view === 'keypad') {
                dialTyped();
            } else {
                choose(options[active]);
            }
        }
    };

    const highlighted = options[active];

    return (
        <div className="flex flex-col gap-3 p-4" data-testid="dialer">
            <header className="-mx-4 -mt-4 mb-1 flex h-14 items-center justify-between border-b border-[var(--phq-call-line)] pl-4 pr-2">
                {view === 'keypad' ? (
                    <button
                        type="button"
                        aria-label={intl.formatMessage(m.backToSearch)}
                        onClick={() => setView('search')}
                        className="phq-call-press -ml-2 inline-flex h-9 items-center gap-1 rounded-[10px] px-2 text-base font-semibold hover:bg-[var(--phq-call-surface-muted)]"
                    >
                        <ChevronLeft size={18} aria-hidden="true"/>
                        {intl.formatMessage(m.title)}
                    </button>
                ) : (
                    <h2 className="text-base font-semibold">{intl.formatMessage(m.title)}</h2>
                )}
                <button
                    type="button"
                    aria-label={intl.formatMessage(m.close)}
                    onClick={onClose}
                    className="phq-call-press inline-flex h-9 w-9 items-center justify-center rounded-[10px] hover:bg-[var(--phq-call-surface-muted)]"
                >
                    <X size={18} aria-hidden="true"/>
                </button>
            </header>
            <LinePicker/>
            {notice && <NoticeRow notice={notice}/>}
            {refusal && !notice && <RefusalRow refusal={refusal}/>}
            {view === 'search' ? (
                <>
                    {picked.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="dialer-picked">
                            {picked.map(user => {
                                const name = fullName(user.firstName, user.lastName);
                                return (
                                    <span key={String(user.userId ?? user.id)} className="inline-flex h-7 items-center gap-1 rounded-full bg-[var(--phq-call-brand-tint)] pl-2.5 pr-1 text-[13px] font-semibold text-[var(--phq-call-brand-ink)]">
                                        {name}
                                        <button
                                            type="button"
                                            aria-label={intl.formatMessage(m.removePerson, {name})}
                                            onClick={() => unpick(user)}
                                            className="inline-flex h-5 w-5 items-center justify-center rounded-full hover:bg-[var(--phq-call-surface)]"
                                        >
                                            <X size={12} aria-hidden="true"/>
                                        </button>
                                    </span>
                                );
                            })}
                        </div>
                    )}
                    <label className="flex h-11 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 focus-within:shadow-[inset_0_0_0_2px_var(--phq-call-brand-focus)]">
                        <Search size={16} className="shrink-0 text-[var(--phq-call-ink-muted)]" aria-hidden="true"/>
                        <input
                            ref={input}
                            type="search"
                            name="dialer"
                            phq-id="dialpad-typedNumberInput"
                            role="combobox"
                            aria-expanded={options.length > 0}
                            aria-controls={listId}
                            aria-activedescendant={highlighted ? `${listId}-${highlighted.id}` : undefined}
                            aria-invalid={invalid || undefined}
                            aria-label={intl.formatMessage(m.searchPlaceholder)}
                            placeholder={intl.formatMessage(m.searchPlaceholder)}
                            value={text}
                            onChange={event => setText(event.target.value)}
                            onKeyDown={onKeyDown}
                            className="min-w-0 flex-1 bg-transparent text-base font-medium outline-none placeholder:text-[var(--phq-call-ink-muted)]"
                        />
                    </label>
                    {invalid && <p role="alert" className="-mt-1 text-[13px] font-medium text-[var(--phq-call-danger)]">{intl.formatMessage(m.invalidNumber)}</p>}
                    {options.length > 0 && (
                        <div className="flex flex-col gap-1">
                            <p className="px-2 pt-1 text-[13px] font-semibold text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.contacts)}</p>
                            <div id={listId} role="listbox" aria-label={intl.formatMessage(m.matching)} className="flex max-h-60 flex-col overflow-y-auto">
                                {options.map((option, index) => {
                                    const selected = index === active;
                                    const name = optionName(option);
                                    const subline = option.kind === 'team'
                                        ? option.user.position ?? option.user.email
                                        : option.kind === 'contact'
                                            ? getFormatedPhoneNumber(option.contact.phoneNumber, false)
                                            : undefined;
                                    return (
                                        <div
                                            key={option.id}
                                            id={`${listId}-${option.id}`}
                                            role="option"
                                            aria-selected={selected}
                                            onMouseEnter={() => setActive(index)}
                                            onClick={() => choose(option)}
                                            className={`flex h-12 cursor-pointer items-center gap-2.5 rounded-[10px] px-2 ${selected ? 'bg-[var(--phq-call-brand-tint-subtle)]' : ''}`}
                                        >
                                            {option.kind === 'number' ? (
                                                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[var(--phq-call-surface-muted)]">
                                                    <Phone size={16} aria-hidden="true"/>
                                                </span>
                                            ) : (
                                                <CallAvatar identity={{name, userId: option.kind === 'team' ? String(option.user.userId ?? option.user.id) : undefined}} size={32}/>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold">{name}</p>
                                                {subline && <p className="truncate text-[13px] text-[var(--phq-call-ink-muted)]">{subline}</p>}
                                            </div>
                                            {selected && <span className="shrink-0 text-[13px] text-[var(--phq-call-ink-strong-muted)]">{intl.formatMessage(m.enter)}</span>}
                                            {groupCalls && option.kind === 'team' && !pickedIds.has(String(option.user.userId ?? option.user.id)) && (
                                                <button
                                                    type="button"
                                                    data-testid="dialer-add-person"
                                                    aria-label={intl.formatMessage(m.addToCall, {name})}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        pick(option.user);
                                                    }}
                                                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] text-[var(--phq-call-ink-strong-muted)] hover:bg-[var(--phq-call-surface)]"
                                                >
                                                    <UserPlus size={16} aria-hidden="true"/>
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {options.length === 0 && text.trim().length >= MIN_SEARCH && !looksLikeNumber(text) && (
                        <p className="px-2 text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.noMatches)}</p>
                    )}
                    {groupFailed && <p role="alert" className="text-[13px] font-medium text-[var(--phq-call-danger)]">{intl.formatMessage(m.groupCallFailed)}</p>}
                    {picked.length > 0 ? (
                        <div className="flex flex-col gap-1 pt-1">
                            {picked.length > 1 && <p className="px-1 text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.groupCallCount, {count: picked.length + 1})}</p>}
                            <button
                                type="button"
                                data-testid="dialer-call"
                                disabled={startingGroup || notice === 'offline'}
                                onClick={() => void callPicked()}
                                className="phq-call-press inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                            >
                                {startingGroup ? <Loader2 size={16} className="animate-spin" aria-hidden="true"/> : <Phone size={16} aria-hidden="true"/>}
                                <span className="truncate">
                                    {picked.length === 1
                                        ? intl.formatMessage(m.callName, {name: fullName(picked[0].firstName, picked[0].lastName)})
                                        : intl.formatMessage(m.startGroupCall)}
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div className="flex gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setView('keypad')}
                                className="phq-call-press inline-flex h-10 items-center gap-2 rounded-[10px] bg-[var(--phq-call-surface-muted)] px-3 text-sm font-medium"
                            >
                                <Grid3x3 size={16} aria-hidden="true"/>
                                {intl.formatMessage(m.keypad)}
                            </button>
                            <button
                                type="button"
                                data-testid="dialer-call"
                                disabled={!highlighted || isBlocked(highlighted)}
                                onClick={() => choose(highlighted)}
                                className="phq-call-press inline-flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-3 text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                            >
                                <span className="truncate">{highlighted ? intl.formatMessage(m.callName, {name: optionName(highlighted)}) : intl.formatMessage(m.call)}</span>
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="flex h-12 items-center gap-2">
                        <span className="w-9 shrink-0"/>
                        <input
                            ref={input}
                            name="dialer"
                            phq-id="dialpad-typedNumberInput"
                            inputMode="tel"
                            aria-label={intl.formatMessage(m.enterNumber)}
                            placeholder={intl.formatMessage(m.enterNumber)}
                            value={text}
                            onChange={event => setText(event.target.value)}
                            onKeyDown={onKeyDown}
                            className="min-w-0 flex-1 bg-transparent text-center text-2xl font-semibold leading-[1.2] tabular-nums outline-none placeholder:text-base placeholder:font-medium placeholder:text-[var(--phq-call-ink-muted)]"
                        />
                        <button
                            type="button"
                            aria-label={intl.formatMessage(m.deleteDigit)}
                            tabIndex={text ? 0 : -1}
                            onClick={() => setText(text.slice(0, -1))}
                            className={`phq-call-press inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[var(--phq-call-ink-secondary)] hover:bg-[var(--phq-call-surface-muted)] ${text ? '' : 'invisible'}`}
                        >
                            <Delete size={20} aria-hidden="true"/>
                        </button>
                    </div>
                    {invalid && <p role="alert" className="-mt-2 text-center text-[13px] font-medium text-[var(--phq-call-danger)]">{intl.formatMessage(m.invalidNumber)}</p>}
                    <div className="grid grid-cols-3 gap-2">
                        {KEYPAD_KEYS.map(key => (
                            <button
                                key={key.digit}
                                type="button"
                                aria-label={key.digit}
                                onClick={() => {
                                    playToneForDuration(key.digit);
                                    setText(`${text}${key.digit}`);
                                }}
                                className="phq-call-press flex h-12 flex-col items-center justify-center rounded-[10px] bg-[var(--phq-call-surface-muted)]"
                            >
                                <span className="text-xl font-semibold leading-[1.1]">{key.digit}</span>
                                <span className="h-2.5 text-[11px] font-semibold leading-[10px] tracking-[.08em] text-[var(--phq-call-ink-strong-muted)]">{key.letters}</span>
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        data-testid="dialer-call"
                        disabled={!text.trim() || phoneBlocked}
                        onClick={dialTyped}
                        className="phq-call-press inline-flex h-11 items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] text-sm font-semibold text-white disabled:bg-[var(--phq-call-brand-disabled)]"
                    >
                        <Phone size={16} aria-hidden="true"/>
                        {intl.formatMessage(m.call)}
                    </button>
                </>
            )}
        </div>
    );
};
